/**
 * Display labels for a conversation message's author, by who sent it.
 *
 * Single source of truth so a wording change (e.g. "Operator" → "Staff") is one
 * edit instead of a scattered find-and-replace. Used wherever a conversation is
 * flattened into human-readable text — handover transcripts and CRM session
 * notes today. PBX keeps its own "Operator" terminology; this is the
 * messaging/email conversation vocabulary only.
 */
export const CONVERSATION_AUTHOR_LABEL = {
  /** The end customer / contact. */
  customer: 'Customer',
  /** An AI-generated reply. */
  ai: 'AI',
  /** A human teammate who replied (handover target). */
  staff: 'Staff',
} as const

export type ConversationAuthorKind = keyof typeof CONVERSATION_AUTHOR_LABEL

/**
 * Who to name on a message. A colleague is named only from `sender_name` — never
 * from anything else, and never when the record holds no name.
 */
export function resolveConversationAuthorLabel(input: {
  role: ConversationAuthorKind
  /** The resolved contact title; only used for an inbound message. */
  contactTitle: string
  /** Straight from `content_payload`, so genuinely unknown until narrowed. */
  senderName?: unknown
}): string {
  switch (input.role) {
    case 'customer':
      return input.contactTitle
    case 'ai':
      return CONVERSATION_AUTHOR_LABEL.ai
    case 'staff': {
      const named = typeof input.senderName === 'string' ? input.senderName.trim() : ''
      return named || CONVERSATION_AUTHOR_LABEL.staff
    }
  }
}
